import { useMemo, useReducer } from 'react';
import styled from '@emotion/styled';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { RPC_ERROR_CODES } from 'src/apis/exception';
import { isDefinedRPCException } from 'src/apis/utils';
import AlertModal from 'src/components/AlertModal';
import ModalLayoutBase, { MobileModalType, ModalContentStyle, ModalHeader } from 'src/components/ModalLayout';
import useLogin from 'src/hooks/useLogin';
import { useUpdatePreferredLanguagesMutation } from 'src/hooks/usePreferredLanguagesQuery';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { birthAtom, genderAtom, nicknameAtom, registerAtom } from 'src/stores/register/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { GENDER, SignUpSettingsResponse } from 'src/types/register';
import { ToastType } from 'src/types/Toast';
import { client } from 'src/utils/api';
import BirthdayStep from './Steps/Birthday';
import GenderStep from './Steps/Gender';
import LanguageStep from './Steps/Language';
import NicknameStep from './Steps/Nickname';
import OnboardingStep from './Steps/Onboarding';
import type { Actions, State } from './types';
const ModalLayout = styled(ModalLayoutBase)`
  max-width: 500px;
`;
const ModalContent = styled(ModalContentStyle)`
  display: flex;
  flex-direction: column;
`;
const reducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case 'INPUT_LANGUAGES':
      {
        const {
          value: preferredLanguages
        } = action;
        return {
          ...state,
          preferredLanguages
        };
      }
    case 'MARK_NICKNAME_REQUIRED':
      {
        const {
          exception
        } = action;
        return {
          ...state,
          extra: exception
        };
      }
    case 'NEXT_STEP':
      {
        const {
          step,
          signUpSettings,
          extra
        } = state;
        const shouldReceiveLanguage = signUpSettings?.preferredLanguageSettings != null;
        const shouldReceiveNickname = isDefinedRPCException(extra, RPC_ERROR_CODES.INSUFFICIENT_INFORMATION) && extra.data?.throwable.reason === 'nickname';
        switch (step) {
          case 'BIRTHDAY':
            return {
              ...state,
              step: 'GENDER'
            };
          case 'GENDER':
            {
              if (shouldReceiveLanguage) {
                return {
                  ...state,
                  step: 'LANGUAGE'
                };
              } else if (shouldReceiveNickname) {
                return {
                  ...state,
                  step: 'NICKNAME'
                };
              }
              return {
                ...state,
                step: 'ONBOARDING'
              };
            }
          case 'LANGUAGE':
            {
              if (shouldReceiveNickname) {
                return {
                  ...state,
                  step: 'NICKNAME',
                  nickname: extra.data?.throwable.defaultValue
                };
              }
              return {
                ...state,
                step: 'ONBOARDING'
              };
            }
          case 'NICKNAME':
            return {
              ...state,
              step: 'ONBOARDING'
            };
          case 'ONBOARDING':
          default:
            return state;
        }
      }
    case 'PREV_STEP':
      {
        const {
          step,
          signUpSettings
        } = state;
        const shouldReceiveLanguage = signUpSettings?.preferredLanguageSettings != null;
        switch (step) {
          case 'GENDER':
            return {
              ...state,
              step: 'BIRTHDAY'
            };
          case 'LANGUAGE':
            return {
              ...state,
              step: 'GENDER',
              preferredLanguages: undefined
            };
          case 'NICKNAME':
            return {
              ...state,
              step: shouldReceiveLanguage ? 'LANGUAGE' : 'GENDER'
            };
          case 'ONBOARDING':
          case 'BIRTHDAY':
          default:
            return state;
        }
      }
    default:
      return state;
  }
};
const VividiRegisterMachine = () => {
  const {
    t
  } = useTranslation();
  const {
    isLogin
  } = useLogin();
  const {
    data
  } = useSuspenseQuery({
    queryKey: ['/api/v1/signup-settings'],
    async queryFn() {
      return client.get<SignUpSettingsResponse>('/api/v1/signup-settings');
    },
    meta: {
      withoutAuth: true
    }
  });
  const {
    mutateAsync: updatePreferredLanguages
  } = useUpdatePreferredLanguagesMutation();
  const [state, dispatch] = useReducer(reducer, {
    step: 'BIRTHDAY',
    signUpSettings: data?.data
  });
  const setBirth = useSetAtom(birthAtom);
  const setGender = useSetAtom(genderAtom);
  const setNickname = useSetAtom(nicknameAtom);
  const doRegister = useSetAtom(registerAtom);
  const showModal = useSetAtom(showModalAtom);
  const showToast = useSetAtom(showToastAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const navButtonType = useMemo(() => {
    if (state.step === 'ONBOARDING') {
      return ModalNavButtonType.NONE;
    }
    return ModalNavButtonType.BACK;
  }, [state.step]);
  const handleBack = () => {
    dispatch({
      type: 'PREV_STEP'
    });
  };
  const register = async () => {
    const [, error] = await doRegister({
      cmpConsentsAccepted: true
    });
    if (isDefinedRPCException(error, RPC_ERROR_CODES.AZAR_FEEDBACK)) {
      showModal({
        key: ModalType.AZAR_FEEDBACK_DIALOG,
        component: () => <AlertModal onClose={() => {
          closeModal(ModalType.REGISTER);
        }}>
            {error.data?.throwable.alertBody}
          </AlertModal>
      });
      return false;
    } else if (isDefinedRPCException(error, RPC_ERROR_CODES.INSUFFICIENT_INFORMATION) && error.data?.throwable.reason === 'nickname') {
      dispatch({
        type: 'MARK_NICKNAME_REQUIRED',
        exception: error
      });
      return error;
    } else if (error != null) {
      showToast({
        type: ToastType.ERROR,
        message: t('ERROR_PROBLEM')
      });
      closeModal(ModalType.REGISTER);
      return false;
    }
    return true;
  };
  const registerWithGender = async (gender: GENDER) => {
    setGender(gender);
    if (data?.data.preferredLanguageSettings != null) {
      dispatch({
        type: 'NEXT_STEP'
      });
      return;
    }
    if (await register()) {
      dispatch({
        type: 'NEXT_STEP'
      });
    }
  };
  const registerWithLanguages = async (preferredLanguages: NonNullable<State['preferredLanguages']>) => {
    dispatch({
      type: 'INPUT_LANGUAGES',
      value: preferredLanguages
    });
    const result = await register();
    if (result === false) {
      return;
    }
    const shouldReceiveNickname = typeof result === 'object' && isDefinedRPCException(result, RPC_ERROR_CODES.INSUFFICIENT_INFORMATION) && result.data?.throwable.reason === 'nickname';
    if (result && !shouldReceiveNickname && preferredLanguages.length > 0) {
      await updatePreferredLanguages({
        preferredLanguages
      });
    }
    dispatch({
      type: 'NEXT_STEP'
    });
  };
  const registerWithNickname = async (nickname: string) => {
    if (nickname) setNickname({
      status: 'selected',
      nickname
    });
    dispatch({
      type: 'NEXT_STEP'
    });
  };
  const handleComplete = async () => {
    const {
      preferredLanguages
    } = state;

    /** 추가 정보를 입력해야 해서 아직 가입을 하지 못했을 경우 */
    if (!isLogin) {
      const isSucceeded = await register();
      if (!isSucceeded) {
        return;
      }
      if (isSucceeded && preferredLanguages != null && preferredLanguages.length > 0) {
        await updatePreferredLanguages({
          preferredLanguages
        });
      }
    }
    closeModal(ModalType.LOGIN);
    closeModal(ModalType.REGISTER);
  };
  return <ModalLayout isDim mobileType={MobileModalType.SCREEN_MODAL} mobileBreakpoint='tablet' closeWhenClickOutside={false} closeWithESC={false} navButtonType={navButtonType} mobileNavButtonType={navButtonType} onBack={handleBack} data-sentry-element="ModalLayout" data-sentry-component="VividiRegisterMachine" data-sentry-source-file="Machine.tsx">
      {state.step !== 'ONBOARDING' && <ModalHeader />}
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="Machine.tsx">
        {state.step === 'BIRTHDAY' && <BirthdayStep onNext={value => {
        setBirth(value);
        dispatch({
          type: 'NEXT_STEP'
        });
      }} />}
        {state.step === 'GENDER' && <GenderStep onNext={registerWithGender} />}
        {state.step === 'LANGUAGE' && <LanguageStep onNext={registerWithLanguages} />}
        {state.step === 'NICKNAME' && <NicknameStep defaultValue={state.nickname} onNext={registerWithNickname} />}
        {state.step === 'ONBOARDING' && <OnboardingStep onNext={handleComplete} />}
      </ModalContent>
    </ModalLayout>;
};
export default VividiRegisterMachine;